import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {formatErrors} from '../../../constants';
import {
  dichatGroupsForClientAction,
  dichatGroupAddClientAction,
  dichatGroupRemoveClientAction
} from '../../../actions/dichatGroupAction';

/**
 * Болталки клиентки: где она сейчас и куда её можно вернуть.
 *
 * Порядок задаёт сервер: болталка, из которой её выводили последней, идёт первой
 * и помечена `previous`. Её и подсвечиваем — вернуть чаще всего надо именно
 * туда. Но добавляем всегда в ту, которую выбрал человек: молча увести клиентку
 * в другую комнату значит потом объяснять это поддержке.
 *
 * Показывается только у клиенток канала: у остальных болталки живут в самой
 * платформе (телеграм-группы), и этот список к ним отношения не имеет.
 */
export function ClientGroups({client}) {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const getList = useCallback(() => {
    if (!client || !client.id) {
      return;
    }
    dispatch(dichatGroupsForClientAction(client.id)).then((data) => {
      setItems(Array.isArray(data) ? data : (data.items || []));
    }).catch(err => {
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  }, [dispatch, client]);

  // Список грузим сразу, а не по клику: в свёрнутом виде показывается, где
  // клиентка состоит, и без загрузки там всегда значилось «нет» — даже когда
  // болталка у неё есть
  useEffect(() => {
    getList();
  }, [getList]);

  const handleAdd = (groupId) => {
    if (loading) {
      return;
    }
    setLoading(true);
    dispatch(dichatGroupAddClientAction(groupId, client.id)).then(() => {
      setLoading(false);
      toast.success('Добавлена в болталку', {position: toast.POSITION.BOTTOM_RIGHT});
      getList();
    }).catch(err => {
      setLoading(false);
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  };

  const handleRemove = (groupId) => {
    if (loading) {
      return;
    }
    setLoading(true);
    dispatch(dichatGroupRemoveClientAction(groupId, client.id)).then(() => {
      setLoading(false);
      toast.success('Выведена из болталки', {position: toast.POSITION.BOTTOM_RIGHT});
      getList();
    }).catch(err => {
      setLoading(false);
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  };

  // Перевод: сначала добавляем в новую, потом выводим из прежних.
  //
  // Порядок именно такой. Сорвись что-нибудь посередине, клиентка окажется в
  // двух болталках сразу — это видно в списке и чинится одним нажатием. При
  // обратном порядке она осталась бы без болталки вовсе, и заметить это было бы
  // некому: строка «нет» выглядит как обычное состояние.
  const handleMove = (groupId) => {
    if (loading) {
      return;
    }
    setLoading(true);
    dispatch(dichatGroupAddClientAction(groupId, client.id)).then(() => {
      const leaving = items.filter((i) => i.current && i.id !== groupId);
      return Promise.all(leaving.map((i) => dispatch(dichatGroupRemoveClientAction(i.id, client.id))));
    }).then(() => {
      setLoading(false);
      toast.success('Переведена в другую болталку', {position: toast.POSITION.BOTTOM_RIGHT});
      getList();
    }).catch(err => {
      setLoading(false);
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
      // Состав мог смениться наполовину — показываем, как есть на сервере
      getList();
    });
  };

  const current = items.filter((i) => i.current);

  return <div className="user-info__wrapper">
    <p className="help">Болталки</p>
    <span className="user-info__text">
      {current.length > 0 ? current.map((i) => i.title).join(', ') : 'нет'}
    </span>
    <span className="dropdown--link pointer" style={{display: 'block', marginTop: '6px'}}
          onClick={() => setOpen(value => !value)}>
      {open ? 'Свернуть' : 'Изменить'}
    </span>

    {open && <div style={{marginTop: '8px'}}>
      {items.length === 0 && <p className="help">Болталок пока нет</p>}
      {items.map((i) => <div key={i.id}
                             style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                     gap: '8px', padding: '4px 0'}}>
        <span style={i.previous && !i.current ? {fontWeight: 'bold'} : null}>
          {i.title}
          {i.previous && !i.current && <span className="help" style={{marginLeft: '6px'}}>была здесь</span>}
          <span className="help" style={{marginLeft: '6px'}}>участниц: {i.members}</span>
        </span>
        {/* Одна болталка на клиентку: пока она где-то состоит, чужие болталки
            предлагают только перевод. «Добавить» появляется, когда болталки нет
            вовсе, — иначе одним нажатием её можно было бы завести во вторую, а
            это не то, чего от кнопки ждут */}
        {!i.current && current.length > 0 && <button type="button" className="btn" disabled={loading}
                                                     onClick={() => handleMove(i.id)}>Перевести</button>}
        {!i.current && current.length === 0 && <button type="button" className="btn" disabled={loading}
                                                       onClick={() => handleAdd(i.id)}>Добавить</button>}
        {i.current && <button type="button" className="btn btn-red" disabled={loading}
                              onClick={() => handleRemove(i.id)}>Убрать</button>}
      </div>)}
    </div>}
  </div>;
}
